import { useState, useEffect } from 'react';
import {
  Box,
  Stack,
  Flex,
  Text,
  Button,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  IconButton,
  HStack,
  SimpleGrid,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Tooltip as ChakraTooltip,
  Menu,
  MenuButton,
  MenuList,
  MenuOptionGroup,
  MenuItemOption,
  MenuDivider,
  chakra,
} from '@chakra-ui/react';
// Using react-icons for chevron to avoid @chakra-ui/icons dependency
import { useParams, useNavigate } from 'react-router-dom';
import { HelivanText } from '../helivan/HelivanText';
import { HelivanColor } from '../helivan/HelivanColor';
import {
  SurveyQuestionType,
  SurveyType,
  useGetSurveyByIdQuery,
} from '../../generated/graphql';
import { FaChevronLeft, FaChevronRight, FaChevronDown } from 'react-icons/fa';
import { IoArrowBackOutline, IoSparklesOutline } from 'react-icons/io5';
import {
  PieChart,
  Pie,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  ResponsiveContainer,
} from 'recharts';
import { HelivanLabel } from '../helivan/HelivanLabel';
import { RiInformationLine } from 'react-icons/ri';

// Color palette for charts
const CHART_COLORS = [
  '#0088FE',
  '#00C49F',
  '#FFBB28',
  '#FF8042',
  '#8884D8',
  '#82CA9D',
  '#FFC658',
  '#8DD1E1',
];

export const SurveyResponses = () => {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [currentResponseIndex, setCurrentResponseIndex] = useState(0);

  const { data, loading, error } = useGetSurveyByIdQuery({
    variables: { id: surveyId || '' },
    skip: !surveyId,
  });

  // Filter state (empty means no filter -> show all)
  const [selectedAges, setSelectedAges] = useState<string[]>([]);
  const [selectedGenders, setSelectedGenders] = useState<string[]>([]);
  const [selectedEthnicities, setSelectedEthnicities] = useState<string[]>([]);

  // Filter option constants
  const AGE_OPTIONS = [
    'Under 18',
    '18-24',
    '25-34',
    '35-44',
    '45-54',
    '55-64',
    '65+',
  ];
  const ETHNICITY_OPTIONS = [
    'White',
    'Black or African American',
    'Hispanic or Latino',
    'Asian',
    'Native American',
    'Pacific Islander',
    'Other',
  ];
  const GENDER_OPTIONS = ['Male', 'Female', 'Other'];

  useEffect(() => {
    if (error) {
      toast({
        title: 'Error loading survey',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [error, toast]);

  const survey = data?.getSurveyById;
  const responses = survey?.responses || [];
  const questions = survey?.questions || [];

  const anyFilterActive =
    selectedAges.length > 0 ||
    selectedGenders.length > 0 ||
    selectedEthnicities.length > 0;

  const clearFilters = () => {
    setSelectedAges([]);
    setSelectedGenders([]);
    setSelectedEthnicities([]);
  };

  // Get respondent metadata for a SurveyResponse
  // Prefer response.digitalTwin (new schema), fallback to questionResponse.digitalTwin (old schema)
  const getResponseUserData = (response: any) => {
    const responseLevel = response?.digitalTwin?.surveyUserData;
    if (responseLevel) return responseLevel;
    const firstQr = response?.questionResponses?.[0];
    const qrLevel = firstQr?.digitalTwin?.surveyUserData;
    return qrLevel || {};
  };

  // AND across categories, OR within each category
  const filterResponsesBySelected = (sourceResponses: any[]) => {
    if (!anyFilterActive) return sourceResponses;
    return sourceResponses.filter((resp) => {
      const ud = getResponseUserData(resp);

      // Age filter (OR within)
      if (selectedAges.length > 0) {
        if (!ud?.age || !selectedAges.includes(ud.age)) return false;
      }

      // Gender filter (OR within)
      if (selectedGenders.length > 0) {
        if (!ud?.gender || !selectedGenders.includes(ud.gender)) return false;
      }

      // Ethnicity filter (OR within)
      if (selectedEthnicities.length > 0) {
        if (!ud?.ethnicity || !selectedEthnicities.includes(ud.ethnicity)) {
          return false;
        }
      }

      return true;
    });
  };

  // Navigation handlers for individual responses
  const handlePreviousResponse = () => {
    if (currentResponseIndex > 0) {
      setCurrentResponseIndex(currentResponseIndex - 1);
    }
  };

  const handleNextResponse = () => {
    if (currentResponseIndex < responses.length - 1) {
      setCurrentResponseIndex(currentResponseIndex + 1);
    }
  };

  // Render individual question response
  const renderQuestionResponse = (
    questionResponse: any,
    questionType: SurveyQuestionType
  ) => {
    return (
      <Text>{questionResponse.response?.join(', ') || 'No response'}</Text>
    );
  };

  // Questions Tab Content
  const renderQuestionsTab = () => {
    if (loading) {
      return <Text>Loading questions...</Text>;
    }

    if (!questions || questions.length === 0) {
      return <Text color="gray.500">No questions found for this survey.</Text>;
    }

    // If this is an AB test, show messages side by side
    if (survey?.type === SurveyType.AbTesting && questions.length === 2) {
      const extractMessage = (title: string) => {
        const prefix = survey?.baseQuestion
          ? `${survey?.baseQuestion}:`
          : 'Do you agree with the following statement?: ';
        return title.startsWith(`${prefix}###`)
          ? title.slice(prefix.length + 3)
          : title;
      };

      const messageA = extractMessage(questions[0].title);
      const messageB = extractMessage(questions[1].title);

      return (
        <Stack spacing={4}>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <Card variant="outline">
              <CardHeader>
                <Heading size="md">Message A</Heading>
              </CardHeader>
              <CardBody>
                <Box
                  as="textarea"
                  value={messageA}
                  readOnly
                  width="100%"
                  minH="150px"
                  p={3}
                  bg="gray.50"
                  borderRadius="md"
                  border="1px solid"
                  borderColor="gray.200"
                  resize="none"
                  fontFamily="inherit"
                  fontSize="md"
                  pointerEvents="none"
                  cursor="default"
                  _focus={{ outline: 'none', boxShadow: 'none' }}
                />
              </CardBody>
            </Card>
            <Card variant="outline">
              <CardHeader>
                <Heading size="md">Message B</Heading>
              </CardHeader>
              <CardBody>
                <Box
                  as="textarea"
                  value={messageB}
                  readOnly
                  width="100%"
                  minH="150px"
                  p={3}
                  bg="gray.50"
                  borderRadius="md"
                  border="1px solid"
                  borderColor="gray.200"
                  resize="none"
                  fontFamily="inherit"
                  fontSize="md"
                  pointerEvents="none"
                  cursor="default"
                  _focus={{ outline: 'none', boxShadow: 'none' }}
                />
              </CardBody>
            </Card>
          </SimpleGrid>
        </Stack>
      );
    }

    // Otherwise show regular questionnaire questions
    return (
      <Stack spacing={4}>
        {questions.map((question, index) => (
          <Card key={question.id} variant="outline">
            <CardBody>
              <Stack spacing={2}>
                <Flex justifyContent="space-between" alignItems="center">
                  <Text fontWeight="bold" fontSize="lg">
                    Question {index + 1}
                  </Text>
                  <HelivanLabel
                    label={
                      question.type === SurveyQuestionType.MultipleChoice
                        ? 'Multiple Choice'
                        : question.type === SurveyQuestionType.MultiSelect
                          ? 'Multi Select'
                          : question.type === SurveyQuestionType.Text
                            ? 'Short Text'
                            : question.type === SurveyQuestionType.TextLong
                              ? 'Text'
                              : question.type === SurveyQuestionType.TextNumeric
                                ? 'Numeric'
                                : ''
                    }
                    variant={
                      question.type === SurveyQuestionType.MultipleChoice
                        ? 'blue'
                        : question.type === SurveyQuestionType.MultiSelect
                          ? 'success'
                          : question.type === SurveyQuestionType.Text
                            ? 'green'
                            : question.type === SurveyQuestionType.TextLong
                              ? 'error'
                              : question.type === SurveyQuestionType.TextNumeric
                                ? 'green'
                                : 'blue'
                    }
                    size="lg"
                  />
                </Flex>
                <Text>{question.title}</Text>
              </Stack>
            </CardBody>
          </Card>
        ))}
      </Stack>
    );
  };

  // Individual Responses View
  const renderIndividualResponses = () => {
    if (loading) {
      return <Text>Loading responses...</Text>;
    }

    if (!responses || responses.length === 0) {
      return (
        <Box p={6} textAlign="center">
          <Text color="gray.500">No responses yet for this survey.</Text>
        </Box>
      );
    }

    const currentResponse = responses[currentResponseIndex];

    return (
      <Stack spacing={6}>
        {/* Navigation Header */}
        <Flex justifyContent="space-between" alignItems="center">
          <IconButton
            aria-label="Previous response"
            icon={<FaChevronLeft />}
            onClick={handlePreviousResponse}
            isDisabled={currentResponseIndex === 0}
            variant="outline"
          />
          <Text fontWeight="medium">
            Response {currentResponseIndex + 1} of {responses.length}
          </Text>
          <IconButton
            aria-label="Next response"
            icon={<FaChevronRight />}
            onClick={handleNextResponse}
            isDisabled={currentResponseIndex === responses.length - 1}
            variant="outline"
          />
        </Flex>

        {/* Response Details */}
        <Box p={5} borderWidth="1px" borderRadius="md" bg="white" shadow="sm">
          <Flex justifyContent="space-between" alignItems="center" mb={4}>
            <HelivanText variant="subheader">
              Respondent #{currentResponseIndex + 1}
            </HelivanText>
            <Text fontSize="sm" color="gray.500">
              {currentResponse.createdAt
                ? new Date(currentResponse.createdAt).toLocaleString()
                : 'Date unknown'}
            </Text>
          </Flex>

          <Stack spacing={4}>
            {currentResponse.questionResponses?.map((questionResponse) => (
              <Box
                key={questionResponse.id}
                p={4}
                bg="gray.50"
                borderRadius="md"
              >
                <Text fontWeight="medium" mb={2}>
                  {questionResponse.question.title}
                </Text>
                {renderQuestionResponse(
                  questionResponse,
                  questionResponse.question.type
                )}
              </Box>
            ))}
          </Stack>
        </Box>
      </Stack>
    );
  };

  // Prepare data for charts
  const prepareChartData = (question: any, sourceResponses?: any[]) => {
    const dataset = sourceResponses ?? responses;
    const responseData: { [key: string]: number } = {};

    dataset.forEach((response) => {
      const questionResponse = response.questionResponses?.find(
        (qr: any) => qr.question.id === question.id
      );

      if (questionResponse) {
        questionResponse.response?.forEach((response: string) => {
          responseData[response] = (responseData[response] || 0) + 1;
        });
      }
    });

    return Object.entries(responseData).map(([name, value]) => ({
      name,
      value,
    }));
  };

  // Prepare AB Test Histogram Data
  const prepareABTestData = (sourceResponses?: any[]) => {
    const dataset = sourceResponses ?? responses;

    if (questions.length !== 2) {
      return { data: [], range: 0 };
    }

    const questionA = questions[0];
    const questionB = questions[1];

    // Calculate preference spread for each response
    const preferenceDistribution: { [key: number]: number } = {};
    // Track respondents by spread for demographic breakdown
    const respondentsBySpread: { [key: number]: any[] } = {};
    let maxSpreadObserved = 0;
    let minSpreadObserved = 0;

    // Calculate the theoretical maximum possible spread
    // This is based on the total response counts across all responses
    let maxPossibleSpread = 0;

    dataset.forEach((response) => {
      const qrA = response.questionResponses?.find(
        (qr: any) => qr.question.id === questionA.id
      );
      const qrB = response.questionResponses?.find(
        (qr: any) => qr.question.id === questionB.id
      );

      if (qrA?.responseCounts && qrB?.responseCounts) {
        // Calculate the spread for question A (first option count - second option count)
        const countsA = qrA.responseCounts;
        const totalCountA = countsA.reduce(
          (sum: number, rc: any) => sum + (rc.count || 0),
          0
        );
        const spreadA =
          countsA.length >= 2
            ? (countsA[0]?.count || 0) - (countsA[1]?.count || 0)
            : countsA[0]?.count || 0;

        // Calculate the spread for question B
        const countsB = qrB.responseCounts;
        const totalCountB = countsB.reduce(
          (sum: number, rc: any) => sum + (rc.count || 0),
          0
        );
        const spreadB =
          countsB.length >= 2
            ? (countsB[0]?.count || 0) - (countsB[1]?.count || 0)
            : countsB[0]?.count || 0;

        // Total spread: difference between A's preference and B's preference
        const totalSpread = spreadA - spreadB;

        // Track this spread
        preferenceDistribution[totalSpread] =
          (preferenceDistribution[totalSpread] || 0) + 1;

        // Track respondents for demographic breakdown
        if (!respondentsBySpread[totalSpread]) {
          respondentsBySpread[totalSpread] = [];
        }
        respondentsBySpread[totalSpread].push(response);

        // Update observed range
        if (totalSpread > maxSpreadObserved) {
          maxSpreadObserved = totalSpread;
        }
        if (totalSpread < minSpreadObserved) {
          minSpreadObserved = totalSpread;
        }

        // Update theoretical max (each question could have all answers on one side)
        const questionMaxSpread = Math.max(totalCountA, totalCountB);
        if (questionMaxSpread > maxPossibleSpread) {
          maxPossibleSpread = questionMaxSpread;
        }
      }
    });

    // Use the maximum of observed range or theoretical range to ensure full scale
    const range = Math.max(
      Math.abs(maxSpreadObserved),
      Math.abs(minSpreadObserved),
      maxPossibleSpread
    );

    // Create a complete dataset with all integer values from -range to +range
    // This ensures consistent bar widths and proper positioning
    const data = [];
    for (let spread = -range; spread <= range; spread++) {
      data.push({
        spread: spread,
        count: preferenceDistribution[spread] || 0,
        respondents: respondentsBySpread[spread] || [],
      });
    }

    return { data, range };
  };

  // Render AB Test Histogram
  const renderABTestHistogram = () => {
    if (loading) {
      return <Text>Loading AB test results...</Text>;
    }

    if (!responses || responses.length === 0) {
      return (
        <Box p={6} textAlign="center">
          <Text color="gray.500">No responses yet for this AB test.</Text>
        </Box>
      );
    }

    if (questions.length !== 2) {
      return (
        <Box p={6} textAlign="center">
          <Text color="red.500">
            AB tests require exactly 2 questions. This survey has{' '}
            {questions.length} questions.
          </Text>
        </Box>
      );
    }

    const filteredResponses = filterResponsesBySelected(responses);
    const { data, range } = prepareABTestData(filteredResponses);
    const questionA = questions[0];
    const questionB = questions[1];

    return (
      <Stack spacing={6}>
        {/* Filters */}
        <Flex width="full" justifyContent="space-between" alignItems="center">
          <Menu closeOnSelect={false}>
            <MenuButton
              as={Button}
              rightIcon={<FaChevronDown />}
              variant="outline"
            >
              Filters
            </MenuButton>
            <MenuList minW="300px" maxH="300px" overflowY="auto">
              <MenuOptionGroup
                title="Age"
                type="checkbox"
                value={selectedAges}
                onChange={(vals) => setSelectedAges(vals as string[])}
              >
                {AGE_OPTIONS.map((opt) => (
                  <MenuItemOption key={opt} value={opt}>
                    {opt}
                  </MenuItemOption>
                ))}
              </MenuOptionGroup>
              <MenuDivider />
              <MenuOptionGroup
                title="Gender"
                type="checkbox"
                value={selectedGenders}
                onChange={(vals) => setSelectedGenders(vals as string[])}
              >
                {GENDER_OPTIONS.map((opt) => (
                  <MenuItemOption key={opt} value={opt}>
                    {opt}
                  </MenuItemOption>
                ))}
              </MenuOptionGroup>
              <MenuDivider />
              <MenuOptionGroup
                title="Ethnicity"
                type="checkbox"
                value={selectedEthnicities}
                onChange={(vals) => setSelectedEthnicities(vals as string[])}
              >
                {ETHNICITY_OPTIONS.map((opt) => (
                  <MenuItemOption key={opt} value={opt}>
                    {opt}
                  </MenuItemOption>
                ))}
              </MenuOptionGroup>
            </MenuList>
          </Menu>
          <Button
            size="sm"
            variant="ghost"
            onClick={clearFilters}
            isDisabled={!anyFilterActive}
          >
            Clear
          </Button>
        </Flex>

        {/* Filter badges */}
        <HStack spacing={2}>
          {selectedAges.length > 0 &&
            selectedAges.map((age) => (
              <HelivanLabel key={age} label={age} variant="blue" size="lg" />
            ))}
          {selectedGenders.length > 0 &&
            selectedGenders.map((gender) => (
              <HelivanLabel
                key={gender}
                label={gender}
                variant="blueDark"
                size="lg"
              />
            ))}
          {selectedEthnicities.length > 0 &&
            selectedEthnicities.map((ethnicity) => (
              <HelivanLabel
                key={ethnicity}
                label={ethnicity}
                variant="success"
                size="lg"
              />
            ))}
        </HStack>

        <Card variant="outline">
          <CardHeader justifyContent="center" width="full">
            <Text textAlign="center" fontWeight="bold" fontSize="lg">
              {survey?.baseQuestion != null && survey?.baseQuestion.length > 0
                ? survey.baseQuestion
                : 'Do you agree with the following statement?'}
            </Text>
          </CardHeader>
          <CardBody>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="spread"
                  type="number"
                  domain={[-range - 0.5, range + 0.5]}
                  allowDataOverflow={false}
                  tick={false}
                  axisLine={true}
                />
                <YAxis
                  label={{
                    value: 'Number of Respondents',
                    angle: -90,
                    position: 'insideLeft',
                  }}
                  allowDecimals={false}
                />
                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const spread = payload[0].payload.spread;
                      const count = payload[0].value;
                      const respondents = payload[0].payload.respondents || [];

                      // Only show tooltip if count > 0
                      if (count === 0) {
                        return null;
                      }

                      // Calculate percentage based on total response count
                      // For each respondent, get their total count and calculate preference percentage
                      let totalResponseCount = 0;
                      let avgPreferencePercentage = 0;

                      respondents.forEach((resp: any) => {
                        const qrA = resp.questionResponses?.find(
                          (qr: any) => qr.question.id === questionA.id
                        );
                        const qrB = resp.questionResponses?.find(
                          (qr: any) => qr.question.id === questionB.id
                        );

                        if (qrA?.responseCounts && qrB?.responseCounts) {
                          const countsA = qrA.responseCounts;
                          const totalCountA = countsA.reduce(
                            (sum: number, rc: any) => sum + (rc.count || 0),
                            0
                          );
                          const countsB = qrB.responseCounts;
                          const totalCountB = countsB.reduce(
                            (sum: number, rc: any) => sum + (rc.count || 0),
                            0
                          );

                          const totalCount = totalCountA + totalCountB;

                          if (totalCount > 0) {
                            // Calculate spread
                            const spreadA =
                              countsA.length >= 2
                                ? (countsA[0]?.count || 0) -
                                  (countsA[1]?.count || 0)
                                : countsA[0]?.count || 0;
                            const spreadB =
                              countsB.length >= 2
                                ? (countsB[0]?.count || 0) -
                                  (countsB[1]?.count || 0)
                                : countsB[0]?.count || 0;

                            const totalSpread = spreadB - spreadA;

                            // Percentage is |spread| / totalCount * 100
                            const percentage = Math.round(
                              (Math.abs(totalSpread) / totalCount) * 100
                            );
                            avgPreferencePercentage += percentage;
                            totalResponseCount++;
                          }
                        }
                      });

                      const preferencePercentage =
                        totalResponseCount > 0
                          ? Math.round(
                              avgPreferencePercentage / totalResponseCount
                            )
                          : 0;

                      // Calculate demographics
                      const demographics = {
                        age: {} as { [key: string]: number },
                        gender: {} as { [key: string]: number },
                        ethnicity: {} as { [key: string]: number },
                      };

                      respondents.forEach((resp: any) => {
                        const userData = getResponseUserData(resp);
                        if (userData.age) {
                          demographics.age[userData.age] =
                            (demographics.age[userData.age] || 0) + 1;
                        }
                        if (userData.gender) {
                          demographics.gender[userData.gender] =
                            (demographics.gender[userData.gender] || 0) + 1;
                        }
                        if (userData.ethnicity) {
                          demographics.ethnicity[userData.ethnicity] =
                            (demographics.ethnicity[userData.ethnicity] || 0) +
                            1;
                        }
                      });

                      return (
                        <Box
                          bg="white"
                          p={3}
                          border="1px solid"
                          borderColor="gray.200"
                          borderRadius="md"
                          shadow="md"
                          maxW="300px"
                        >
                          <Text fontWeight="bold" mb={2}>
                            {spread === 0
                              ? 'No Preference'
                              : spread > 0
                                ? `Prefer Message B by ${preferencePercentage}%`
                                : `Prefer Message A by ${preferencePercentage}%`}
                          </Text>
                          <Text fontSize="sm" mb={2}>
                            {count} respondent{count !== 1 ? 's' : ''}
                          </Text>

                          {/* Demographics breakdown */}
                          {Object.keys(demographics.age).length > 0 && (
                            <Box mb={1}>
                              <Text
                                fontSize="xs"
                                fontWeight="semibold"
                                color="gray.600"
                              >
                                Age:
                              </Text>
                              {Object.entries(demographics.age).map(
                                ([age, cnt]) => (
                                  <Text
                                    key={age}
                                    fontSize="xs"
                                    color="gray.700"
                                  >
                                    {age}: {cnt}
                                  </Text>
                                )
                              )}
                            </Box>
                          )}

                          {Object.keys(demographics.gender).length > 0 && (
                            <Box mb={1}>
                              <Text
                                fontSize="xs"
                                fontWeight="semibold"
                                color="gray.600"
                              >
                                Gender:
                              </Text>
                              {Object.entries(demographics.gender).map(
                                ([gender, cnt]) => (
                                  <Text
                                    key={gender}
                                    fontSize="xs"
                                    color="gray.700"
                                  >
                                    {gender}: {cnt}
                                  </Text>
                                )
                              )}
                            </Box>
                          )}

                          {Object.keys(demographics.ethnicity).length > 0 && (
                            <Box>
                              <Text
                                fontSize="xs"
                                fontWeight="semibold"
                                color="gray.600"
                              >
                                Ethnicity:
                              </Text>
                              {Object.entries(demographics.ethnicity).map(
                                ([ethnicity, cnt]) => (
                                  <Text
                                    key={ethnicity}
                                    fontSize="xs"
                                    color="gray.700"
                                  >
                                    {ethnicity}: {cnt}
                                  </Text>
                                )
                              )}
                            </Box>
                          )}
                        </Box>
                      );
                    }
                    return null;
                  }}
                />
                <Bar dataKey="count" fill="#0088FE" />
              </BarChart>
            </ResponsiveContainer>
            <Flex justifyContent="space-between" alignItems="center" mt={4}>
              <Stack spacing={1} maxW="45%">
                <Text fontSize="sm" color="gray.600" fontWeight={600}>
                  ← Preference for Message A
                </Text>
                <Text fontSize="sm" color="gray.600">
                  {questionA.title.split(
                    'Do you agree with the following statement?: '
                  ).length > 1
                    ? questionA.title.split(
                        'Do you agree with the following statement?: '
                      )[1]
                    : questionA.title.split('###').length > 1
                      ? questionA.title.split('###')[1]
                      : questionA.title}
                </Text>
              </Stack>

              <Stack spacing={1} alignItems="flex-end" maxW="45%">
                <Box width="200px">
                  <Text fontSize="sm" color="gray.600" fontWeight={600}>
                    Preference for Message B →
                  </Text>
                </Box>
                <Text fontSize="sm" color="gray.600">
                  {questionB.title.split(
                    'Do you agree with the following statement?: '
                  ).length > 1
                    ? questionB.title.split(
                        'Do you agree with the following statement?: '
                      )[1]
                    : questionB.title.split('###').length > 1
                      ? questionB.title.split('###')[1]
                      : questionB.title}
                </Text>
              </Stack>
            </Flex>
            <Box mt={4}>
              <HelivanText
                variant="largeBody+"
                colorOverride={HelivanColor.text.helivanBlue}
              >
                Total Responses:{' '}
                <chakra.span fontWeight="normal" color="black">
                  {filteredResponses.length}
                </chakra.span>{' '}
              </HelivanText>
            </Box>
          </CardBody>
        </Card>

        {/* Insights Section */}
        <Card variant="outline">
          <CardHeader>
            <HStack spacing={2}>
              <IoSparklesOutline
                color={HelivanColor.text.helivanBlue}
                size={20}
              />
              <HelivanText
                variant="largeBody+"
                colorOverride={HelivanColor.text.helivanBlue}
              >
                Key Insights
              </HelivanText>
            </HStack>
          </CardHeader>
          <CardBody>
            {(() => {
              // Calculate demographic preferences (only counting non-zero spreads)
              const demographicPreferences: {
                [key: string]: {
                  total: number;
                  totalWithPreference: number;
                  totalPercentageSum: number;
                  avgPreferencePercentage: number;
                  avgSpread: number;
                };
              } = {};

              filteredResponses.forEach((response) => {
                const qrA = response.questionResponses?.find(
                  (qr: any) => qr.question.id === questionA.id
                );
                const qrB = response.questionResponses?.find(
                  (qr: any) => qr.question.id === questionB.id
                );

                if (qrA?.responseCounts && qrB?.responseCounts) {
                  const countsA = qrA.responseCounts;
                  const totalCountA = countsA.reduce(
                    (sum: number, rc: any) => sum + (rc.count || 0),
                    0
                  );
                  const spreadA =
                    countsA.length >= 2
                      ? (countsA[0]?.count || 0) - (countsA[1]?.count || 0)
                      : countsA[0]?.count || 0;

                  const countsB = qrB.responseCounts;
                  const totalCountB = countsB.reduce(
                    (sum: number, rc: any) => sum + (rc.count || 0),
                    0
                  );
                  const spreadB =
                    countsB.length >= 2
                      ? (countsB[0]?.count || 0) - (countsB[1]?.count || 0)
                      : countsB[0]?.count || 0;

                  const totalSpread = spreadA - spreadB;
                  const totalCount = totalCountA + totalCountB;
                  const hasPreference = totalSpread !== 0;

                  // Calculate preference percentage: |spread| / totalCount * 100
                  const preferencePercentage =
                    totalCount > 0
                      ? (Math.abs(totalSpread) / totalCount) * 100
                      : 0;

                  const userData = getResponseUserData(response);

                  // Track by age
                  if (userData.age) {
                    const key = `Age: ${userData.age}`;
                    if (!demographicPreferences[key]) {
                      demographicPreferences[key] = {
                        total: 0,
                        totalWithPreference: 0,
                        totalPercentageSum: 0,
                        avgPreferencePercentage: 0,
                        avgSpread: 0,
                      };
                    }
                    demographicPreferences[key].total++;
                    // Only count if there's an actual preference
                    if (hasPreference) {
                      demographicPreferences[key].totalWithPreference++;
                      demographicPreferences[key].totalPercentageSum +=
                        preferencePercentage;
                      demographicPreferences[key].avgSpread += totalSpread; // Track sign for direction
                    }
                  }

                  // Track by gender
                  if (userData.gender) {
                    const key = `Gender: ${userData.gender}`;
                    if (!demographicPreferences[key]) {
                      demographicPreferences[key] = {
                        total: 0,
                        totalWithPreference: 0,
                        totalPercentageSum: 0,
                        avgPreferencePercentage: 0,
                        avgSpread: 0,
                      };
                    }
                    demographicPreferences[key].total++;
                    if (hasPreference) {
                      demographicPreferences[key].totalWithPreference++;
                      demographicPreferences[key].totalPercentageSum +=
                        preferencePercentage;
                      demographicPreferences[key].avgSpread += totalSpread;
                    }
                  }

                  // Track by ethnicity
                  if (userData.ethnicity) {
                    const key = `Ethnicity: ${userData.ethnicity}`;
                    if (!demographicPreferences[key]) {
                      demographicPreferences[key] = {
                        total: 0,
                        totalWithPreference: 0,
                        totalPercentageSum: 0,
                        avgPreferencePercentage: 0,
                        avgSpread: 0,
                      };
                    }
                    demographicPreferences[key].total++;
                    if (hasPreference) {
                      demographicPreferences[key].totalWithPreference++;
                      demographicPreferences[key].totalPercentageSum +=
                        preferencePercentage;
                      demographicPreferences[key].avgSpread += totalSpread;
                    }
                  }
                }
              });

              // Calculate average preference percentages (only from those with actual preferences)
              Object.keys(demographicPreferences).forEach((key) => {
                const data = demographicPreferences[key];
                if (data.totalWithPreference > 0) {
                  data.avgPreferencePercentage =
                    data.totalPercentageSum / data.totalWithPreference;
                  data.avgSpread = data.avgSpread / data.totalWithPreference; // For direction
                } else {
                  data.avgPreferencePercentage = 0;
                  data.avgSpread = 0;
                }
              });

              // Find demographics with strongest preferences
              type DemographicStat = { demographic: string; avgSpread: number };
              let mostPrefersB: DemographicStat | null = null;
              let mostPrefersA: DemographicStat | null = null;
              let leastPreference: DemographicStat | null = null;

              Object.entries(demographicPreferences).forEach(([demo, data]) => {
                // Most prefers B (positive spread)
                if (!mostPrefersB || data.avgSpread > mostPrefersB.avgSpread) {
                  mostPrefersB = {
                    demographic: demo,
                    avgSpread: data.avgSpread,
                  };
                }

                // Most prefers A (negative spread)
                if (!mostPrefersA || data.avgSpread < mostPrefersA.avgSpread) {
                  mostPrefersA = {
                    demographic: demo,
                    avgSpread: data.avgSpread,
                  };
                }

                // Least preference (closest to 0)
                if (
                  !leastPreference ||
                  Math.abs(data.avgSpread) < Math.abs(leastPreference.avgSpread)
                ) {
                  leastPreference = {
                    demographic: demo,
                    avgSpread: data.avgSpread,
                  };
                }
              });

              // Get lists of demographics preferring A or B
              const demographicsPreferringA: string[] = [];
              const demographicsPreferringB: string[] = [];

              Object.entries(demographicPreferences).forEach(([demo, data]) => {
                // Only consider demographics with avgPreferencePercentage > 10% (meaningful preference)
                if (data.avgPreferencePercentage > 10) {
                  // Use avgSpread sign to determine direction
                  if (data.avgSpread < 0) {
                    demographicsPreferringA.push(demo);
                  } else if (data.avgSpread > 0) {
                    demographicsPreferringB.push(demo);
                  }
                }
              });

              // Extract values for rendering
              type DemographicStatWithPercentage = {
                demographic: string;
                avgSpread: number;
                avgPreferencePercentage: number;
              };

              const prefersB: DemographicStatWithPercentage | null =
                mostPrefersB
                  ? {
                      demographic: (mostPrefersB as DemographicStat)
                        .demographic,
                      avgSpread: (mostPrefersB as DemographicStat).avgSpread,
                      avgPreferencePercentage:
                        demographicPreferences[
                          (mostPrefersB as DemographicStat).demographic
                        ]?.avgPreferencePercentage || 0,
                    }
                  : null;

              const prefersA: DemographicStatWithPercentage | null =
                mostPrefersA
                  ? {
                      demographic: (mostPrefersA as DemographicStat)
                        .demographic,
                      avgSpread: (mostPrefersA as DemographicStat).avgSpread,
                      avgPreferencePercentage:
                        demographicPreferences[
                          (mostPrefersA as DemographicStat).demographic
                        ]?.avgPreferencePercentage || 0,
                    }
                  : null;

              const leastPref: DemographicStatWithPercentage | null =
                leastPreference
                  ? {
                      demographic: (leastPreference as DemographicStat)
                        .demographic,
                      avgSpread: (leastPreference as DemographicStat).avgSpread,
                      avgPreferencePercentage:
                        demographicPreferences[
                          (leastPreference as DemographicStat).demographic
                        ]?.avgPreferencePercentage || 0,
                    }
                  : null;

              return (
                <Stack spacing={3}>
                  {prefersB && (
                    <Box>
                      <Text fontSize="sm">
                        <Text
                          as="span"
                          fontWeight="bold"
                          color={HelivanColor.text.helivanBlue}
                        >
                          Most prefers Message B:
                        </Text>{' '}
                        {prefersB.demographic} (
                        {Math.round(prefersB.avgPreferencePercentage)}% prefer
                        B)
                      </Text>
                    </Box>
                  )}

                  {prefersA && (
                    <Box>
                      <Text fontSize="sm">
                        <Text
                          as="span"
                          fontWeight="bold"
                          color={HelivanColor.text.helivanBlue}
                        >
                          Most prefers Message A:
                        </Text>{' '}
                        {prefersA.demographic} (
                        {Math.round(prefersA.avgPreferencePercentage)}% prefer
                        A)
                      </Text>
                    </Box>
                  )}

                  {leastPref && (
                    <Box>
                      <Text fontSize="sm">
                        <Text
                          as="span"
                          fontWeight="bold"
                          color={HelivanColor.text.helivanBlue}
                        >
                          Smallest preference:
                        </Text>{' '}
                        {leastPref.demographic} (
                        {Math.round(leastPref.avgPreferencePercentage)}%
                        preference strength)
                      </Text>
                    </Box>
                  )}

                  {demographicsPreferringB.length > 0 && (
                    <Box>
                      <Text fontSize="sm">
                        <Text
                          as="span"
                          fontWeight="bold"
                          color={HelivanColor.text.helivanBlue}
                        >
                          Demographics preferring Message B:
                        </Text>{' '}
                        {demographicsPreferringB.join(', ')}
                      </Text>
                    </Box>
                  )}

                  {demographicsPreferringA.length > 0 && (
                    <Box>
                      <Text fontSize="sm">
                        <Text
                          as="span"
                          fontWeight="bold"
                          color={HelivanColor.text.helivanBlue}
                        >
                          Demographics preferring Message A:
                        </Text>{' '}
                        {demographicsPreferringA.join(', ')}
                      </Text>
                    </Box>
                  )}

                  {Object.keys(demographicPreferences).length === 0 && (
                    <Text fontSize="sm" color="gray.500">
                      No demographic data available for analysis.
                    </Text>
                  )}
                </Stack>
              );
            })()}
          </CardBody>
        </Card>
      </Stack>
    );
  };

  // Summary Responses View
  const renderSummaryResponses = () => {
    if (loading) {
      return <Text>Loading summary...</Text>;
    }

    if (!responses || responses.length === 0) {
      return (
        <Box p={6} textAlign="center">
          <Text color="gray.500">No responses yet for this survey.</Text>
        </Box>
      );
    }

    const filteredResponses = filterResponsesBySelected(responses);

    return (
      <Stack spacing={6}>
        <Flex width="full" justifyContent="space-between" alignItems="center">
          <Menu closeOnSelect={false}>
            <MenuButton
              as={Button}
              rightIcon={<FaChevronDown />}
              variant="outline"
            >
              Filters
            </MenuButton>
            <MenuList minW="300px" maxH="300px" overflowY="auto">
              <MenuOptionGroup
                title="Age"
                type="checkbox"
                value={selectedAges}
                onChange={(vals) => setSelectedAges(vals as string[])}
              >
                {AGE_OPTIONS.map((opt) => (
                  <MenuItemOption key={opt} value={opt}>
                    {opt}
                  </MenuItemOption>
                ))}
              </MenuOptionGroup>
              <MenuDivider />
              <MenuOptionGroup
                title="Gender"
                type="checkbox"
                value={selectedGenders}
                onChange={(vals) => setSelectedGenders(vals as string[])}
              >
                {GENDER_OPTIONS.map((opt) => (
                  <MenuItemOption key={opt} value={opt}>
                    {opt}
                  </MenuItemOption>
                ))}
              </MenuOptionGroup>
              <MenuDivider />
              <MenuOptionGroup
                title="Ethnicity"
                type="checkbox"
                value={selectedEthnicities}
                onChange={(vals) => setSelectedEthnicities(vals as string[])}
              >
                {ETHNICITY_OPTIONS.map((opt) => (
                  <MenuItemOption key={opt} value={opt}>
                    {opt}
                  </MenuItemOption>
                ))}
              </MenuOptionGroup>
            </MenuList>
          </Menu>
          <Button
            size="sm"
            variant="ghost"
            onClick={clearFilters}
            isDisabled={!anyFilterActive}
          >
            Clear
          </Button>
        </Flex>
        <HStack spacing={2}>
          {selectedAges.length > 0 &&
            selectedAges.map((age) => (
              <HelivanLabel key={age} label={age} variant="blue" size="lg" />
            ))}
          {selectedGenders.length > 0 &&
            selectedGenders.map((gender) => (
              <HelivanLabel
                key={gender}
                label={gender}
                variant="blueDark"
                size="lg"
              />
            ))}
          {selectedEthnicities.length > 0 &&
            selectedEthnicities.map((ethnicity) => (
              <HelivanLabel
                key={ethnicity}
                label={ethnicity}
                variant="success"
                size="lg"
              />
            ))}
        </HStack>

        <HelivanText variant="largeBody">
          Total Responses: {filteredResponses.length}
        </HelivanText>

        {questions.map((question) => (
          <Card key={question.id} variant="outline">
            <CardHeader>
              <Flex
                width="full"
                justifyContent="space-between"
                alignItems="center"
              >
                <Heading size="md">{question.title}</Heading>
                <HelivanLabel
                  label={
                    question.type === SurveyQuestionType.MultipleChoice
                      ? 'Multiple Choice'
                      : question.type === SurveyQuestionType.MultiSelect
                        ? 'Multi Select'
                        : question.type === SurveyQuestionType.Text
                          ? 'Text'
                          : question.type === SurveyQuestionType.TextLong
                            ? 'Text Long'
                            : question.type === SurveyQuestionType.TextNumeric
                              ? 'Text Numeric'
                              : ''
                  }
                  variant="blue"
                  size="lg"
                />
              </Flex>
            </CardHeader>
            <CardBody>
              {question.type === SurveyQuestionType.MultipleChoice && (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={prepareChartData(question, filteredResponses)}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(entry) => `${entry.name}: ${entry.value}`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {prepareChartData(question, filteredResponses).map(
                        (entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={CHART_COLORS[index % CHART_COLORS.length]}
                          />
                        )
                      )}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              )}

              {question.type === SurveyQuestionType.MultiSelect && (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={prepareChartData(question, filteredResponses)}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" fill="#8884d8">
                      {prepareChartData(question, filteredResponses).map(
                        (entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={CHART_COLORS[index % CHART_COLORS.length]}
                          />
                        )
                      )}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}

              {(question.type === SurveyQuestionType.Text ||
                question.type === SurveyQuestionType.TextLong ||
                question.type === SurveyQuestionType.TextNumeric) && (
                <Stack spacing={2} maxH="300px" overflowY="auto">
                  {filteredResponses.map((response, index) => {
                    const questionResponse = response.questionResponses?.find(
                      (qr: any) => qr.question.id === question.id
                    );
                    const value =
                      questionResponse?.response?.join(', ') || 'No response';

                    if (value) {
                      return (
                        <Box
                          key={index}
                          p={2}
                          bg="gray.50"
                          borderRadius="md"
                          borderLeft="3px solid"
                          borderLeftColor="blue.400"
                        >
                          <Text fontSize="sm">{value}</Text>
                        </Box>
                      );
                    }
                    return null;
                  })}
                </Stack>
              )}
            </CardBody>
          </Card>
        ))}
      </Stack>
    );
  };

  // Responses Tab with Individual/Summary sub-tabs
  const renderResponsesTab = () => {
    // If this is an AB test, show only the histogram
    if (survey?.type === SurveyType.AbTesting) {
      return renderABTestHistogram();
    }

    // Otherwise, show the normal questionnaire tabs
    return (
      <Tabs variant="soft-rounded" colorScheme="blue">
        <TabList>
          <Tab
            _selected={{
              color: HelivanColor.text.blueDark,
              bg: HelivanColor.background.secondary,
            }}
          >
            Individual
          </Tab>
          <Tab
            _selected={{
              color: HelivanColor.text.blueDark,
              bg: HelivanColor.background.secondary,
            }}
          >
            Summary
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel>{renderIndividualResponses()}</TabPanel>
          <TabPanel>{renderSummaryResponses()}</TabPanel>
        </TabPanels>
      </Tabs>
    );
  };

  return (
    <Stack
      width="full"
      height="full"
      spacing={0}
      bg={HelivanColor.background.primary}
      border="1px solid"
      borderColor={HelivanColor.border.primary}
      borderRadius="8px"
      overflow="hidden"
    >
      {/* Header */}
      <Flex
        width="full"
        px="30px"
        py="16px"
        borderBottomColor={HelivanColor.border.primary}
        bg="white"
        borderTopRadius="12px"
        borderBottomWidth="1px"
        justifyContent="flex-start"
        alignItems="center"
        gap={4}
      >
        <Box
          onClick={() => navigate('/surveys')}
          _hover={{ cursor: 'pointer' }}
        >
          <IoArrowBackOutline size={16} />
        </Box>
        <Stack spacing={0}>
          <HStack spacing={2}>
            <HelivanText variant="largeSubheader">
              {loading ? 'Survey Responses' : survey?.title}
            </HelivanText>
            {survey?.description && (
              <ChakraTooltip
                label={survey.description}
                placement="bottom"
                hasArrow
                shouldWrapChildren
              >
                <Box display="inline-flex" alignItems="center">
                  <RiInformationLine size={16} />
                </Box>
              </ChakraTooltip>
            )}
          </HStack>
        </Stack>
      </Flex>

      {/* Main Content with Tabs */}
      <Box p={6} overflowY="auto">
        <Tabs variant="line" colorScheme="blue">
          <TabList justifyContent="center">
            <Tab
              _selected={{
                color: HelivanColor.text.blueDark,
                borderBottomColor: HelivanColor.text.blueDark,
                borderBottomWidth: '2px',
              }}
              _hover={{
                color: HelivanColor.text.blueDark,
                borderBottomColor: HelivanColor.text.blueDark,
                borderBottomWidth: '2px',
              }}
            >
              Responses
            </Tab>
            <Tab
              _selected={{
                color: HelivanColor.text.blueDark,
                borderBottomColor: HelivanColor.text.blueDark,
                borderBottomWidth: '2px',
              }}
              _hover={{
                color: HelivanColor.text.blueDark,
                borderBottomColor: HelivanColor.text.blueDark,
                borderBottomWidth: '2px',
              }}
            >
              Questions
            </Tab>
          </TabList>
          <TabPanels>
            <TabPanel>{renderResponsesTab()}</TabPanel>
            <TabPanel>{renderQuestionsTab()}</TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </Stack>
  );
};
